import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaXTwitter } from "react-icons/fa6";
import { AuthContext, IntlContext } from "context";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "components/ui/accordion";
import { Button } from "components/ui/button";
import { useReferences } from "hooks/reference";
import BrandBlock, { SocialLink } from "./BrandBlock";
import { ROUTES, buildRoute, REGISTER_STEPS } from "routes";

interface FooterLink {
    label: string;
    href: string;
    external?: boolean;
}

const STRIPE_URL =
    "https://stripe.com/fr?gad_campaignid=922532853&gad_source=1&gbraid=0AAAAADKNRO5noz1IXabI6muAyknqDXI5z&gclid=CjwKCAjwnZfPBhAGEiwAzg-VzKTdpuKhm-0s_V3djzfZnoR1e53axrvNYfFPETmJ1ylHneH41kePyRoCsQkQAvD_BwE&utm_adposition=&utm_campaign=EMEA_FR_fr_Google_Search_Brand_Stripe_EXA-922532853&utm_content=737161626383&utm_device=c&utm_matchtype=e&utm_medium=cpc&utm_source=google&utm_term=stripe";
const URSSAF_URL = "https://www.cesu.urssaf.fr/info/accueil.html";

const Footer: React.FC = () => {
    const auth = useContext(AuthContext);
    const intl = useContext(IntlContext);
    const reference = useReferences();

    const fileAssurance = reference.get("file", "assurance");
    const fileCgu = reference.get("file", "cgu");
    const fileMentionLegale = reference.get("file", "mention_legale");
    const filePrivacyPolicy = reference.get("file", "politique_confidentialite");

    const serverUrl = process.env.REACT_APP_SERVER_URL ?? "";

    const urlAssurance = fileAssurance ? `${serverUrl}file/ressource/${fileAssurance.code}` : "#";
    const urlCguMentions = fileCgu
        ? `${serverUrl}file/ressource/${fileCgu.code}`
        : fileMentionLegale
          ? `${serverUrl}file/ressource/${fileMentionLegale.code}`
          : "#";
    const urlPrivacyPolicy = filePrivacyPolicy ? `${serverUrl}file/ressource/${filePrivacyPolicy.code}` : "#";

    /** Returns a `/connexion?redirect=...` href for unauthenticated users, or the real path otherwise. */
    const authHref = (path: string) => (auth.isLoggedIn ? path : buildRoute.loginWithRedirect(path));

    const socialLinks: SocialLink[] = [
        {
            href: "https://www.instagram.com/twoghether",
            label: "Instagram",
            Icon: FaInstagram,
        },
        {
            href: "https://www.facebook.com/profile.php?id=61584816410608",
            label: "Facebook",
            Icon: FaFacebookF,
        },
        {
            href: "https://www.linkedin.com/company/twoghether",
            label: "LinkedIn",
            Icon: FaLinkedinIn,
        }
    ];

    const mySpaceLinks: FooterLink[] = [
        { label: intl.formatMessage("footer.my_space.my_account"), href: authHref(ROUTES.ACCOUNT) },
        { label: intl.formatMessage("footer.my_space.messaging"), href: authHref(ROUTES.MESSAGING) },
        { label: intl.formatMessage("footer.my_space.my_missions"), href: authHref(ROUTES.SERVICE_HISTORY) },
        {
            label: intl.formatMessage("footer.my_space.become_expert"),
            href: buildRoute.registerSub(REGISTER_STEPS.PROVIDER),
        },
        {
            label: intl.formatMessage("footer.my_space.for_companies"),
            href: ROUTES.COMPANIES,
        },
    ];

    const servicesLinks: FooterLink[] = [
        {
            label: intl.formatMessage("footer.services.housekeeping"),
            href: `${ROUTES.PROVIDERS}?filter_categories=HOUSEKEEPING`,
        },
        {
            label: intl.formatMessage("footer.services.handiwork"),
            href: `${ROUTES.PROVIDERS}?filter_categories=HANDIWORK`,
        },
        {
            label: intl.formatMessage("footer.services.gardening"),
            href: `${ROUTES.PROVIDERS}?filter_categories=GARDENING`,
        },
        {
            label: intl.formatMessage("footer.services.assistance"),
            href: `${ROUTES.PROVIDERS}?filter_categories=ASSISTANCE`,
        },
        {
            label: intl.formatMessage("footer.services.tutoring"),
            href: `${ROUTES.PROVIDERS}?filter_categories=PRIVATE_TUTORING`,
        },
    ];

    const aboutLinks: FooterLink[] = [
        {
            label: intl.formatMessage("footer.about.who_are_we"),
            href: ROUTES.ABOUT,
        },
        { label: intl.formatMessage("footer.about.faq"), href: ROUTES.FAQ },
        { label: intl.formatMessage("footer.about.cgu"), href: urlCguMentions, external: true },
        { label: intl.formatMessage("footer.about.privacy"), href: urlPrivacyPolicy, external: true },
        { label: intl.formatMessage("footer.about.insurance"), href: urlAssurance, external: true },
        { label: intl.formatMessage("footer.about.urssaf"), href: URSSAF_URL, external: true },
    ];

    const columns = [
        { id: "my-space", title: intl.formatMessage("footer.my_space.title"), links: mySpaceLinks },
        { id: "services", title: intl.formatMessage("footer.services.title"), links: servicesLinks },
        { id: "about", title: intl.formatMessage("footer.about.title"), links: aboutLinks },
    ];

    return (
        <footer className="bg-slate-900 text-slate-50 m-4 rounded-4xl">
            <div className="mx-auto w-full max-w-[1400px] px-4 pt-12 sm:px-6 lg:px-8">
                <div className="hidden md:flex gap-16">
                    <BrandBlock
                        description={
                            <>
                                {intl.formatMessage("footer.brand_description_line1")}
                                <br />
                                {intl.formatMessage("footer.brand_description_line2")}
                            </>
                        }
                        socialLinks={socialLinks}
                        className="w-1/3"
                    />

                    <div className="flex w-2/3 gap-10">
                        {columns.map(({ id, title, links }) => (
                            <div key={id} className="flex-1">
                                <p className="text-sm font-semibold text-white flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" />
                                    {title}
                                </p>
                                <ul className="mt-6 space-y-4 text-sm">
                                    {links.map(({ label, href, external }) => (
                                        <li key={label}>
                                            {external ? (
                                                <Button variant="footerLink" size="none" asChild>
                                                    <a href={href} target="_blank" rel="noopener noreferrer">
                                                        {label}
                                                    </a>
                                                </Button>
                                            ) : (
                                                <Button variant="footerLink" size="none" asChild>
                                                    <Link to={href}>{label}</Link>
                                                </Button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="md:hidden space-y-8">
                    <BrandBlock
                        description={
                            <>
                                {intl.formatMessage("footer.brand_description_line1")}
                                <br />
                                {intl.formatMessage("footer.brand_description_line2")}
                            </>
                        }
                        socialLinks={socialLinks}
                    />

                    <Accordion type="multiple" className="w-full">
                        {columns.map(({ id, title, links }) => (
                            <AccordionItem key={id} value={id} className="border-white/10">
                                <AccordionTrigger className="text-sm font-semibold text-white hover:no-underline py-3">
                                    {title}
                                </AccordionTrigger>
                                <AccordionContent>
                                    <ul className="space-y-3 pb-2 text-sm">
                                        {links.map(({ label, href, external }) => (
                                            <li key={label}>
                                                {external ? (
                                                    <Button variant="footerLink" size="none" asChild>
                                                        <a href={href} target="_blank" rel="noopener noreferrer">
                                                            {label}
                                                        </a>
                                                    </Button>
                                                ) : (
                                                    <Button variant="footerLink" size="none" asChild>
                                                        <Link to={href}>{label}</Link>
                                                    </Button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>

                <div className="mt-10 py-6 h-full flex flex-col gap-4 border-t border-white/10 text-xs text-slate-700 uppercase sm:flex-row sm:items-center sm:justify-between">
                    <p>
                        © {new Date().getFullYear()} Twoghether — {intl.formatMessage("footer.copyright")}
                    </p>
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-3">
                            <span className="text-xs uppercase tracking-wide">Paiements sécurisés</span>
                            <Button variant="socialIcon" size="logo" asChild>
                                <a
                                    href={STRIPE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={intl.formatMessage("footer.trust_stripe")}
                                    className="text-xs"
                                >
                                    Stripe
                                </a>
                            </Button>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-xs uppercase tracking-wide">Protection</span>
                            <Button variant="socialIcon" size="logo" asChild>
                                <a
                                    href={urlAssurance}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={intl.formatMessage("footer.trust_axa")}
                                    className="text-xs"
                                >
                                    AXA
                                </a>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
